import { ApiResult, request, safeRequest } from '@synergy/api';
import { DocumentType } from '@synergy/models';
import { getUrlSegment } from '@synergy/utils';

import { Activity } from '../models/activity';
import { ActivityComment } from '../models/activity-comment';
import { DocumentComment } from '../models/document-comment';
import { DocumentStreamEntry, DocumentStreamKind, DocumentStreamPage } from '../models/document-stream-entry';

export class ActivityApi {
    constructor(protected baseUrl: string) {}

    public addComment(
        activityId: number,
        text: string,
        documentType: DocumentType
    ): Promise<ApiResult<ActivityComment>> {
        return safeRequest(
            () =>
                request.post(`${this.baseUrl}/activities/${getUrlSegment(documentType)}/${activityId}/comments`, {
                    text,
                }),
            response => ActivityComment.fromPlainObject(response.data)
        );
    }

    public updateComment(
        activityId: number,
        commentId: number,
        text: string,
        documentType: DocumentType
    ): Promise<ApiResult<ActivityComment>> {
        return safeRequest(
            () =>
                request.patch(
                    `${this.baseUrl}/activities/${getUrlSegment(documentType)}/${activityId}/comments/${commentId}`,
                    { text }
                ),
            response => ActivityComment.fromPlainObject(response.data)
        );
    }

    public deleteComment(
        activityId: number,
        commentId: number,
        documentType: DocumentType
    ): Promise<ApiResult<boolean>> {
        return safeRequest(
            () =>
                request.delete(
                    `${this.baseUrl}/activities/${getUrlSegment(documentType)}/${activityId}/comments/${commentId}`
                ),
            response => response.status === 204
        );
    }

    public getActivities(documentType: DocumentType, documentId: number): Promise<ApiResult<Activity[]>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/activities/${getUrlSegment(documentType)}/${documentId}`),
            response => response.data.items.map(Activity.fromPlainObject)
        );
    }

    /**
     * The whole history of a document — activity log and transitions in one
     * chronological, pageable stream (#2154).
     */
    public getDocumentStream(
        documentType: DocumentType,
        documentId: number,
        page: number,
        limit: number,
        kind?: DocumentStreamKind
    ): Promise<ApiResult<DocumentStreamPage>> {
        return safeRequest(
            () =>
                request.get(`${this.baseUrl}/document-stream/${getUrlSegment(documentType)}/${documentId}`, {
                    params: { page, limit, ...(kind ? { kind } : {}) },
                }),
            // `items`, not `_embedded`: the response interceptor in
            // libs/api renames it on every paginated response.
            response => ({
                entries: response.data.items.map(DocumentStreamEntry.fromPlainObject),
                total: response.data.total,
                page: response.data.page,
                pages: response.data.pages,
            })
        );
    }

    /**
     * The same stream for every document of the procurement chain, narrowable
     * to one kind of document (#2160).
     */
    public getDocumentChainStream(
        documentType: DocumentType,
        documentId: number,
        page: number,
        limit: number,
        kind?: DocumentStreamKind,
        stage?: string
    ): Promise<ApiResult<DocumentStreamPage>> {
        return safeRequest(
            () =>
                request.get(`${this.baseUrl}/document-chain/${getUrlSegment(documentType)}/${documentId}/stream`, {
                    params: { page, limit, ...(kind ? { kind } : {}), ...(stage ? { stage } : {}) },
                }),
            response => ({
                entries: response.data.items.map(DocumentStreamEntry.fromPlainObject),
                total: response.data.total,
                page: response.data.page,
                pages: response.data.pages,
            })
        );
    }

    /** Write a remark on the document; it joins the stream as an internal entry (#2156). */
    public addDocumentComment(
        documentType: DocumentType,
        documentId: number,
        text: string
    ): Promise<ApiResult<DocumentComment>> {
        return safeRequest(
            () =>
                request.post(`${this.baseUrl}/document-stream/${getUrlSegment(documentType)}/${documentId}/comments`, {
                    text,
                }),
            response => DocumentComment.fromPlainObject(response.data)
        );
    }

    public updateDocumentComment(commentId: number, text: string): Promise<ApiResult<DocumentComment>> {
        return safeRequest(
            () => request.patch(`${this.baseUrl}/document-comments/${commentId}`, { text }),
            response => DocumentComment.fromPlainObject(response.data)
        );
    }

    public deleteDocumentComment(commentId: number): Promise<ApiResult<boolean>> {
        return safeRequest(
            () => request.delete(`${this.baseUrl}/document-comments/${commentId}`),
            response => response.status === 204
        );
    }
}
